import { store } from '../redux';
import { logoutSession, setRedirectUrl } from '../redux/session/actions';
import { getToken } from '../redux/session/selectors';
import { createValidRoute } from '../utils/components';

const handleResponse = async (
  res: Response,
  methodToUse: keyof Response = 'json'
): Promise<any> => {
  if (res.ok) {
    if (res.status === 204) {
      return null;
    }
    return (res[methodToUse] as CallableFunction)();
  }
  if (res.status === 401) {
    store.dispatch(logoutSession());
  } else if (res.status === 403) {
    store.dispatch(setRedirectUrl(window.location as any));
    window.location.href = createValidRoute('SIGN_UP');
  }
  throw await res.json();
};

export const API_ROOT = process.env.REACT_APP_API_BASE_URL || '/api';

// Methods
const get: FetchFunction = (url, config) =>
  fetch(`${API_ROOT}/${url}`, {
    ...config,
    method: 'GET',
  }).then(handleResponse);

const getFile: FetchFunction = (url, config) =>
  fetch(`${API_ROOT}/${url}`, {
    ...config,
    method: 'GET',
  }).then(r => handleResponse(r, 'blob'));

const post: FetchFunction = (url, config) =>
  fetch(`${API_ROOT}/${url}`, {
    ...config,
    method: 'POST',
  }).then(handleResponse);

const patch: FetchFunction = (url, config) =>
  fetch(`${API_ROOT}/${url}`, {
    ...config,
    method: 'PATCH',
  }).then(handleResponse);

const put: FetchFunction = (url, config) =>
  fetch(`${API_ROOT}/${url}`, {
    ...config,
    method: 'PUT',
  }).then(handleResponse);

const del: FetchFunction = (url, config) =>
  fetch(`${API_ROOT}/${url}`, {
    ...config,
    method: 'DELETE',
  }).then(handleResponse);

const AuthMiddleWare: FetchMiddleware = fn => (url, config) => {
  let conf = config;
  const isProtectedRoute = !url.startsWith('auth');
  if (isProtectedRoute) {
    const token = getToken(store.getState());
    conf = addAuthHeader(config, token); // TODO: get Token from AUTH Service;
  }

  return fn(url, conf);
};

const addAuthHeader: RequestModifier<string> = (config = {}, token) => ({
  ...config,
  headers: { ...(config.headers || {}), 'X-Auth-Token': token },
});

function API() {
  const getAPI = AuthMiddleWare(get);
  const getFileAPI = AuthMiddleWare(getFile);
  const patchAPI = AuthMiddleWare(patch);
  const postAPI = AuthMiddleWare(post);
  const putAPI = AuthMiddleWare(put);
  const deleteAPI = AuthMiddleWare(del);
  return {
    BASE_URL: '2019-02-19/',
    delete: deleteAPI,
    get: getAPI,
    getFile: getFileAPI,
    patch: patchAPI,
    post: postAPI,
    put: putAPI,
  };
}

export default API();

type RequestModifier<T> = (c: RequestConfig, token: T) => RequestInit;
type FetchFunction = (url: string, config?: RequestConfig) => Promise<any>;
type FetchMiddleware = (fn: FetchFunction) => FetchFunction;
type RequestConfig = RequestInit | undefined;
